import { useEffect, useState } from "react";
import { ListItem, Text, TextButton } from "@artprov/design-system"
import PropTypes from "prop-types";
import Alert from "../../../common/components/alert/Alert.jsx";
import formatCurrencyForUi from "../../../common/utils/functions/formatCurrencyForUi.js";
import EditInvoiceModal from "./EditInvoiceModal.jsx";
import { renderEstado } from "../../../common/utils/functions/renderEstado.jsx";
import { useFacturaStore } from "../../../common/zustand/factura.store.js";
import { useComprasAuth } from "../../../common/hooks/useComprasAuth.js";
import { isClosedStatus } from "../../../common/utils/functions/invoiceStatus.js";

export default function InfoInvoice({ data, idComprobante }) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState("");
  const facturaActual = useFacturaStore((state) => state.facturaActual);
  const setFacturaActual = useFacturaStore((state) => state.setFacturaActual);
  const { userDetails } = useComprasAuth();
  const validateUsername = !!userDetails?.userPurchasePermissions;

  useEffect(() => {
    if (!facturaActual && data && Object.keys(data || {}).length > 0) {
      setFacturaActual(data);
    }
  }, [data, facturaActual, setFacturaActual]);

  const current = facturaActual || data || {};

  const getItemsFactura = () => {
    return [
      {
        title: 'Tipo',
        content: current.TIPO
      },
      {
        title: 'Número',
        content: current.NRO_COMPROBANTE
      },
      {
        title: 'Fecha de emisión',
        content: current.FECHA_EMISION
      },
      {
        title: 'Importe',
        content: `$ ${formatCurrencyForUi(current.IMPORTE)}`
      },
      {
        title: 'Orden de compra',
        content: current.ORDEN_COMPRA
      },
      {
        title: 'Número de despacho',
        content: current.NUMERO_DESPACHO
      },
      {
        title: 'Estado de factura',
        content: renderEstado(current.ESTADOCOMPROBANTE)
      },
    ];
  }
  const itemsFactura = getItemsFactura();

  const handleEditFacturaClick = () => {
    setIsEditModalOpen(true);
  }

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
  }

  const handleEditSuccess = ({ purchaseOrder, dispatchNumber } = {}) => {
    const prev = current || {};
    const next = {
      ...prev,
      ORDEN_COMPRA: purchaseOrder ?? prev?.ORDEN_COMPRA,
      NUMERO_DESPACHO: dispatchNumber ?? prev?.NUMERO_DESPACHO,
    };
    setFacturaActual(next);
    setShowSuccessAlert(true);
    setShowErrorAlert(false);
    setErrorAlertMessage("");
  };

  const handleEditError = (message = "") => {
    setErrorAlertMessage(message || "Ha ocurrido un error. Verifique los datos.");
    setShowErrorAlert(true);
  };
  
  const canEdit =
    validateUsername && !isClosedStatus(current.ESTADOCOMPROBANTE);

  return (
    <div className="border border-gray-300 rounded p-6 bg-white">
      {showSuccessAlert && (
        <Alert
          variant="success"
          description="Edición de factura procesada correctamente"
          onClose={() => setShowSuccessAlert(false)}
          autoHideMs={5000}
        />
      )}
      {showErrorAlert && (
        <Alert
          variant="error"
          description={errorAlertMessage}
          onClose={() => setShowErrorAlert(false)}
        />
      )}

      <Text variant="body1" className="mb-4 font-semibold text-tertiary-10">
        Información de la factura
      </Text>
      <ListItem
        classNameContainer=''
        classNameTitle='mr-24'
        classNameContent='w-full sm:w-auto text-left'
        maxItems={itemsFactura.length}
        items={itemsFactura}
      />
      {canEdit && (
        <div className="flex justify-end mt-4">
          <TextButton
            onClick={handleEditFacturaClick}
            IconRight="edit"
            size="small"
            classNameDescription="font-semibold"
          >
            Editar datos
          </TextButton>
        </div>
      )}
      <EditInvoiceModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        initialData={current}
        idComprobante={idComprobante}
        onSuccess={handleEditSuccess}
        onError={handleEditError}
      />
    </div>
  );
}

InfoInvoice.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  idComprobante: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
